import { type AxiosError } from 'axios'
import { makeAutoObservable } from 'mobx'

import { AuthController } from '@App/api'
import { BrowserLocalstorage } from '@App/store'
import { type IUserData, type IAuthDto, type IUpdatePasswordDto } from '@App/types/authType'
import { type IApiErrorHandler } from '@App/types/common'
import { onHandleResponseNotification } from '@App/utils'

class Auth {
    login: string = ''
    password: string = ''
    userData: IUserData | null = null
    isFetching: boolean = false

    constructor() {
        makeAutoObservable(this)
    }

    // actions
    onSetIsFetching(isFetching: boolean) {
        this.isFetching = isFetching
    }

    onSetCreds(login: string, password: string) {
        this.login = login
        this.password = password
    }

    onSetUserData(userData: IUserData | null) {
        this.userData = userData
    }

    onLogout() {
        BrowserLocalstorage.removeToken()
        this.onSetUserData(null)
    }

    // requests
    async onGetTokenByCreds(dto: IAuthDto) {
        this.onSetIsFetching(true)
        const client = new AuthController()
        try {
            const { data } = await client.onGetTokenByCreds(dto)
            if (data.accessToken) {
                BrowserLocalstorage.setToken(data.accessToken)
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }

    async onGetUserData() {
        const client = new AuthController()
        try {
            const { data } = await client.onGetUserData()
            // const foundRole = onSetMainRole(data.roles)
            this.onSetUserData(data)
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            BrowserLocalstorage.removeToken()
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }

    async onSignOut(callback: () => void) {
        const client = new AuthController()
        this.onSetUserData(null)
        try {
            await client.onSignOut()
            this.onLogout()
        } catch (e) {
            this.onLogout()
        } finally {
            callback()
        }
    }

    async onChangeAdminPassword(dto: IUpdatePasswordDto, onSuccess: () => void) {
        const client = new AuthController()
        try {
            const { status } = await client.onChangeAdminPassword(dto)
            onHandleResponseNotification(status, `Пароль успешно обновлен`)
            onSuccess()
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }
}

export default new Auth()
